import { useCallback, useEffect, useState } from "react";
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { FormValidator, getImageList, populatedCategories } from "../_utils";

type IDeliveryStepType = "greeting_card" | "gift_card" | "gift_message" | "card_delivery";

const useGiftCardStepManager = ({
  digitalEnabled,
  physicalEnabled,
  bulkOrder,
  defaultStep,
  formData,
  getProxyValidationConfig,
  settings,
  translations,
  maxLinesReached,
  deliveryLeadTime,
  updateFormErrors,
  fulfillmentType,
  inDashboard
}: any) => {


  // Within a try catch block, so that we can use it outside of react router
  const location  = (() => {

    try {
      var tryLocation = useLocation();
      return tryLocation;
    } catch(e) {
      return window.location;
    }

  })();

  const { type } = useParams();

  const navigate   = window.GCPSDK.navigate = useNavigate();

  const urlParams  = new URLSearchParams(location.search);

  const [currentStep, setCurrentStep] = useState<IDeliveryStepType>("greeting_card");

  const [validationConfig, setValidationConfig] = useState<any>({});

  const shouldShowDeliveryStep = (digitalEnabled && physicalEnabled && !bulkOrder);

  /**
   * Navigate to a specific step
   * @param step 
   */
  const goToStep = (step: IDeliveryStepType) => {
   
    if (step === "card_delivery") {
      const deliveryUrl = `${window.location.pathname.replace("physical/", "").replace("digital/", "").replace("purchase", "")}?step=${step}`;
      navigate(deliveryUrl);
    }

    if (inDashboard) {
      setCurrentStep(step);
    } else if (!type) {
      navigate(`${window.location.pathname}${fulfillmentType}/purchase?step=${step}`);
    } else {
      navigate(`${window.location.pathname}?step=${step}`);
    }
    
  };

  /**
   * Get the current step from the URL
   * @param defaultStep 
   * @returns 
   */
  const getStep = (defaultStep: IDeliveryStepType): IDeliveryStepType => {
    
    const allowedSteps: IDeliveryStepType[] = ["greeting_card", "gift_card", "gift_message", "card_delivery"];

    const fallbackStep: IDeliveryStepType = "greeting_card";
    const stepParam = urlParams.get('step') as IDeliveryStepType;

    if (stepParam && allowedSteps.includes(stepParam)) {
      return (stepParam === "card_delivery" && !shouldShowDeliveryStep) ? fallbackStep : stepParam;
    }

    if (defaultStep) {
      return defaultStep;
    }

    return (shouldShowDeliveryStep) ? "card_delivery" : fallbackStep;
  };

  const step: IDeliveryStepType = getStep(defaultStep);
  
  /**
   * Get the next form step
   * @param {*} currentStep 
   * @returns 
   */
  const getNextStep = (currentStep: string) => {
    
    const categories    = populatedCategories(settings, fulfillmentType);
    const categoryCount = categories ? categories.length : 0;
    const imageList     = categoryCount > 0 ? getImageList(categories[0], settings, fulfillmentType) : [];
    const hasSingleImageOption = categoryCount <= 1 && imageList.length <= 1;

    switch(currentStep) {
      case "card_delivery":
        return hasSingleImageOption ? "gift_card" : "greeting_card";
      case "greeting_card":
        return "gift_card";
      case "gift_card":
        return "gift_message";
      default:
        return "gift_message"
    }
  };

  /**
   * Handle step form validation.
   * @param {object} validationConfig
   * @param {string} step
   */
  const validateStep = (
    currentFormData: any,
    validationConfig: any, 
    step: any,
  ) => {
    const inputRules = validationConfig[step];
    const valid = FormValidator.validate(inputRules, currentFormData);

    
    return valid;
  };
  
  /**
   * Navigate to next step if current step is complete.
   * @param {object} event
   * @returns
   */
  const handleNextStep = useCallback((currentFormData: any, config: any) => {

    console.log("handleNextStep", currentFormData, config, currentStep);
    const isValid  = validateStep(currentFormData, config, currentStep);
    const nextStep = getNextStep(currentStep);

    let stepErrors: any = {...FormValidator.getErrors()};
    console.log("stepErrors", stepErrors);
    if (isValid === true) {
      updateFormErrors({});
      goToStep(nextStep);
    } else {
      updateFormErrors(stepErrors);
      console.warn("Step incomplete", stepErrors, nextStep);
    }

    

    if (typeof(stepErrors._gcp_selected_card_image) != 'undefined' && stepErrors._gcp_selected_card_image.error === true && !inDashboard) {
      alert(stepErrors._gcp_selected_card_image.message);
    }

  }, [validationConfig, currentStep, formData]);

  /**
   * Handle form submission.
   * @param {object} event
   */
  const validateAllSteps = useCallback((currentFormData: any, config: any) => {

    const stepOneValid   = validateStep(currentFormData, config, "greeting_card");
    const stepOneErrors: any = {...FormValidator.getErrors()};
    const stepTwoValid   = validateStep(currentFormData, config, "gift_card");
    const stepTwoErrors: any = {...FormValidator.getErrors()};
    const stepThreeValid = validateStep(currentFormData, config, "gift_message");
    const stepThreeErrors: any = {...FormValidator.getErrors()};

    
    const isValid = stepOneValid && stepTwoValid && stepThreeValid;
    const currentErrors = {...stepOneErrors, ...stepTwoErrors, ...stepThreeErrors};
    if (isValid) {
      updateFormErrors(currentErrors);
    } else {
      console.warn("One or more steps are incomplete", {
        errors: currentErrors,
        stepOneValid,
        stepTwoValid,
        stepThreeValid
      });
      updateFormErrors(currentErrors);
    }

    
    if (typeof(currentErrors._gcp_selected_card_image) != 'undefined' && currentErrors._gcp_selected_card_image.error === true && !inDashboard) {
      alert(currentErrors._gcp_selected_card_image.message);
    }

    return isValid

  }, [validationConfig, formData, setValidationConfig, updateFormErrors]);


  useEffect(() => {

    if (!fulfillmentType || !settings || !formData) {
      return;
    }

    const newValidationConfig = getProxyValidationConfig(FormValidator, translations, settings, formData, maxLinesReached, deliveryLeadTime, fulfillmentType);
    
    if (typeof(window.GCP) != 'undefined' && typeof(window.GCP.productPage) != 'undefined') {
      window.GCP.productPage.state.validationConfig = newValidationConfig;
    }

    setValidationConfig(newValidationConfig);

  }, [FormValidator, translations, settings, formData, maxLinesReached, deliveryLeadTime, fulfillmentType]);

  const res = {
    currentStep,
    setCurrentStep,
    goToStep,
    getNextStep,
    step,
    validateStep,
    handleNextStep,
    validateAllSteps,
    validationConfig
  }

  return res;

}

export default useGiftCardStepManager;